import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost } from "../api/http";
import { useAuditProgress } from "../hooks/useAuditProgress";
import { useActiveSite } from "../context/ActiveSiteContext";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { parseApiError } from "../utils/apiErrors";
import { isValidWebsiteUrl } from "../utils/urlValidation";
import SimpleUrlInput from "../components/SimpleUrlInput";
import KPICard from "../components/KPICard";
import PlanGateBanner from "../components/shared/PlanGateBanner";
import DataTable from "../components/shared/DataTable";
import { formatDateTime } from "../utils/dateTime";

const MAX_PAGES_LIMIT = 10;
const MIN_PAGES_LIMIT = 1;
const CONCURRENCY_OPTIONS = [1, 2, 3, 4, 5];

function extractRows(res) {
  const body = res?.data;
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
}

function shortUrl(url) {
  if (!url) return "";
  try {
    const value = String(url).replace(/^https?:\/\//i, "").replace(/\/$/, "");
    return value.length > 44 ? `${value.slice(0, 41)}...` : value;
  } catch {
    return url;
  }
}

function urlMatchesActiveSite(url, activeSite) {
  if (!activeSite || !url) return false;
  try {
    const activeHost = new URL(activeSite.url).hostname.replace(/^www\./i, "").toLowerCase();
    const targetHost = new URL(url).hostname.replace(/^www\./i, "").toLowerCase();
    return activeHost === targetHost || url.toLowerCase().includes(activeHost);
  } catch {
    const activeClean = activeSite.url.replace(/^https?:\/\//i, "").replace(/^www\./i, "").toLowerCase();
    const urlClean = url.replace(/^https?:\/\//i, "").replace(/^www\./i, "").toLowerCase();
    return urlClean.includes(activeClean) || activeClean.includes(urlClean);
  }
}

function formatNumber(value) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num.toLocaleString() : "0";
}

function clampInteger(value, min, max, fallback) {
  const parsed = Number.parseInt(String(value ?? "").trim(), 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

function statusTone(fetchStatus) {
  switch (String(fetchStatus || "").toLowerCase()) {
    case "fetched":
      return "ok";
    case "blocked_robots":
    case "non_html":
      return "";
    case "failed":
      return "error";
    default:
      return "";
  }
}

function normalizeProgressPayload(payload, fallbackJob = null) {
  if (!payload) return null;
  if (payload.stats) {
    return payload;
  }
  const derivedPercent = Number.isFinite(Number(payload.percent))
    ? Number(payload.percent)
    : Number.isFinite(Number(payload.progress))
      ? Number(payload.progress)
      : fallbackJob?.status === "completed" || fallbackJob?.status === "failed"
        ? 100
        : 0;
  return {
    label: payload.label || (fallbackJob?.status === "completed" ? "Crawl complete" : "Crawl status"),
    percent: derivedPercent,
    done: fallbackJob?.status === "completed" || payload.done,
    error: payload.error || null,
    report: payload.report || fallbackJob?.report_data || null,
    stats: null,
  };
}

const demoCrawlJobs = [
  {
    id: "demo-1",
    root_url: "https://example.com",
    status: "completed",
    created_at: new Date(Date.now() - 24 * 3600 * 1000).toISOString(),
    max_pages: 100,
    concurrency: 5,
    report_data: {
      summary: {
        pages_crawled: 84,
        orphan_pages: 3,
        weak_pages: 5,
        redirect_chain_pages: 2,
        max_depth: 4,
        avg_inbound_links: "4.2"
      },
      samplePages: [
        { normalized_url: "https://example.com/", depth: 0, inbound_link_count: 24, fetch_status: "fetched", title: "Example Domain" },
        { normalized_url: "https://example.com/about", depth: 1, inbound_link_count: 12, fetch_status: "fetched", title: "About Us" },
        { normalized_url: "https://example.com/blog/seo-tips", depth: 2, inbound_link_count: 8, fetch_status: "fetched", title: "Top 10 SEO Tips" },
        { normalized_url: "https://example.com/services", depth: 1, inbound_link_count: 15, fetch_status: "fetched", title: "Our Services" },
        { normalized_url: "https://example.com/contact", depth: 1, inbound_link_count: 11, fetch_status: "fetched", title: "Contact Us" },
        { normalized_url: "https://example.com/missing-page", depth: 2, inbound_link_count: 0, fetch_status: "failed", title: "Page Not Found" },
        { normalized_url: "https://example.com/redirect-old", depth: 1, inbound_link_count: 1, fetch_status: "fetched", redirect_chain: ["https://example.com/redirect-old", "https://example.com/new"] },
      ]
    }
  },
  {
    id: "demo-2",
    root_url: "https://example.com/blog",
    status: "completed",
    created_at: new Date(Date.now() - 5 * 24 * 3600 * 1000).toISOString(),
    max_pages: 50,
    concurrency: 3,
    report_data: {
      summary: {
        pages_crawled: 42,
        orphan_pages: 1,
        weak_pages: 2,
        redirect_chain_pages: 0,
        max_depth: 3,
        avg_inbound_links: "3.1"
      },
      samplePages: [
        { normalized_url: "https://example.com/blog", depth: 0, inbound_link_count: 10, fetch_status: "fetched", title: "SEO Blog" },
        { normalized_url: "https://example.com/blog/main", depth: 1, inbound_link_count: 5, fetch_status: "fetched", title: "Blog Main Feed" },
        { normalized_url: "https://example.com/blog/categories", depth: 1, inbound_link_count: 3, fetch_status: "fetched", title: "Categories" },
        { normalized_url: "https://example.com/blog/orphan-post", depth: 2, inbound_link_count: 0, fetch_status: "fetched", title: "Hidden Post" },
      ]
    }
  }
];

export default function SiteCrawl() {
  const { user } = useAuth();
  const { showError, showInfo, showSuccess } = useToast();
  const { activeSite, refreshSites } = useActiveSite();
  const { progress, subscribe, reset } = useAuditProgress();
  const [url, setUrl] = useState("");
  const [maxPages, setMaxPages] = useState(5);
  const [concurrency, setConcurrency] = useState(5);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [pagesResult, setPagesResult] = useState({ data: [], pagination: null });
  const [pagesPage, setPagesPage] = useState(1);
  const [pageSearch, setPageSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [issueFilter, setIssueFilter] = useState("");
  const [fetchStatus, setFetchStatus] = useState("");
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState("");
  const idempotencyKeyRef = useRef(null);
  const [pagesError, setPagesError] = useState("");
  const [planGate, setPlanGate] = useState(null);

  const isPremium = user?.limits?.can_site_crawl === true || ["solo", "agency", "premium"].includes(String(user?.plan_name || "").toLowerCase());
  const currentProgress = normalizeProgressPayload(progress, selectedJob);
  const selectedReport = selectedJob?.report_data || currentProgress?.report || null;
  const summary = selectedReport?.summary || {};
  const samplePages = Array.isArray(selectedReport?.samplePages) ? selectedReport.samplePages : [];

  const filteredJobs = useMemo(() => {
    if (!activeSite) return [];
    return jobs.filter((job) => urlMatchesActiveSite(job.root_url, activeSite));
  }, [jobs, activeSite]);

  useEffect(() => {
    if (filteredJobs.length > 0) {
      const alreadySelected = filteredJobs.find(j => j.id === selectedJobId);
      if (!alreadySelected) {
        setSelectedJobId(filteredJobs[0].id);
      }
    } else {
      setSelectedJobId("");
      setSelectedJob(null);
      setPagesResult({ data: [], pagination: null });
    }
  }, [filteredJobs, selectedJobId]);

  useEffect(() => {
    if (activeSite) {
      setUrl(activeSite.url);
    }
  }, [activeSite]);

  const loadJobs = async (preferredJobId = null) => {
    setJobsLoading(true);
    try {
      const { data } = await apiGet("/api/crawl/jobs", { page: 1, limit: 20 });
      const rows = extractRows(data);
      setJobs(rows);
      if (rows.length > 0) {
        const nextId = preferredJobId || selectedJobId || rows[0]?.id || "";
        setSelectedJobId(nextId);
      } else {
        setSelectedJobId("");
        setSelectedJob(null);
        setPagesResult({ data: [], pagination: null });
      }
    } catch (err) {
      const apiData = err?.response?.data || {};
      if (apiData?.code === "PLAN_FEATURE_REQUIRED") {
        setPlanGate(apiData);
      } else {
        showError(parseApiError(err, "Failed to load crawl jobs"));
      }
    } finally {
      setJobsLoading(false);
    }
  };

  useEffect(() => {
    if (!isPremium) {
      setJobs(demoCrawlJobs);
      if (demoCrawlJobs.length > 0) {
        const nextId = selectedJobId || demoCrawlJobs[0].id;
        setSelectedJobId(nextId);
        const match = demoCrawlJobs.find(j => j.id === nextId) || demoCrawlJobs[0];
        setSelectedJob(match);
      }
      setJobsLoading(false);
      return;
    }
    loadJobs();
  }, [isPremium, activeSite]);

  useEffect(() => {
    if (!selectedJobId) return;
    if (!isPremium) {
      const match = demoCrawlJobs.find(j => j.id === selectedJobId);
      if (match) {
        setSelectedJob(match);
      }
      return;
    }
    let active = true;
    const loadDetail = async () => {
      try {
        const { data } = await apiGet(`/api/crawl/jobs/${selectedJobId}`);
        if (!active) return;
        setSelectedJob(data);
      } catch (err) {
        if (!active) return;
        setSelectedJob(null);
        showError(parseApiError(err, "Failed to load crawl details"));
      }
    };
    loadDetail();
    return () => {
      active = false;
    };
  }, [selectedJobId, isPremium, showError]);

  useEffect(() => {
    if (!selectedJobId) return;
    if (!isPremium) {
      const activeDemo = demoCrawlJobs.find(j => j.id === selectedJobId);
      if (activeDemo) {
        let list = activeDemo.report_data.samplePages || [];
        if (appliedSearch) {
          list = list.filter(p => p.normalized_url.toLowerCase().includes(appliedSearch.toLowerCase()));
        }
        if (issueFilter) {
          if (issueFilter === "orphan") list = list.filter(p => p.inbound_link_count === 0);
          if (issueFilter === "weak") list = list.filter(p => p.inbound_link_count <= 1);
          if (issueFilter === "failed") list = list.filter(p => p.fetch_status === "failed");
          if (issueFilter === "redirect") list = list.filter(p => Array.isArray(p.redirect_chain) && p.redirect_chain.length > 1);
        }
        if (fetchStatus) {
          list = list.filter(p => p.fetch_status === fetchStatus);
        }
        setPagesResult({
          data: list,
          pagination: null
        });
      }
      setPagesLoading(false);
      return;
    }
    let active = true;
    setPagesLoading(true);
    setPagesError("");
    apiGet(`/api/crawl/jobs/${selectedJobId}/pages`, {
      page: pagesPage,
      limit: 25,
      search: appliedSearch,
      issue: issueFilter,
      fetchStatus,
    })
      .then(({ data }) => {
        if (!active) return;
        setPagesResult({
          data: extractRows(data),
          pagination: data.pagination || null,
        });
      })
      .catch((err) => {
        if (!active) return;
        setPagesResult({ data: [], pagination: null });
        setPagesError(parseApiError(err, "Failed to load crawled pages"));
      })
      .finally(() => {
        if (active) setPagesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedJobId, pagesPage, appliedSearch, issueFilter, fetchStatus, isPremium]);

  useEffect(() => {
    if (currentProgress?.done) {
      setLaunching(false);
      showSuccess("Site crawl completed.");
      loadJobs(selectedJobId);
      refreshSites();
    }
  }, [currentProgress?.done]);

  useEffect(() => {
    if (currentProgress?.error) {
      setLaunching(false);
      setLaunchError(currentProgress.error);
      showError(currentProgress.error);
      loadJobs(selectedJobId);
    }
  }, [currentProgress?.error]);

  const activeJob = useMemo(() => jobs.find((job) => job.id === selectedJobId) || selectedJob, [jobs, selectedJobId, selectedJob]);
  const isRunning = activeJob?.status === "queued" || activeJob?.status === "running";

  const handleMaxPagesChange = (event) => {
    const raw = event.target.value;
    if (raw === "") {
      setMaxPages("");
      return;
    }
    setMaxPages(clampInteger(raw, MIN_PAGES_LIMIT, MAX_PAGES_LIMIT, MIN_PAGES_LIMIT));
  };

  const handleMaxPagesBlur = () => {
    setMaxPages((current) => clampInteger(current, MIN_PAGES_LIMIT, MAX_PAGES_LIMIT, MIN_PAGES_LIMIT));
  };

  const startCrawl = async (event) => {
    event.preventDefault();
    if (!isPremium) {
      setPlanGate({
        code: "PLAN_FEATURE_REQUIRED",
        cta: "Upgrade to Premium to unlock site-wide crawl jobs.",
      });
      showInfo("Upgrade to Premium to launch site crawls.");
      return;
    }
    if (!isValidWebsiteUrl(url)) {
      showError("Please enter a valid public website URL.");
      return;
    }

    setLaunching(true);
    setLaunchError("");
    setPlanGate(null);
    reset();

    // Generate a unique idempotency key for this crawl request
    // Prevents duplicate crawls if the user double-clicks or retries
    const key = `crawl_${url}_${Date.now()}`;
    idempotencyKeyRef.current = key;

    try {
      const { data } = await apiPost("/api/crawl/run", {
        rootUrl: url,
        maxPages: clampInteger(maxPages, MIN_PAGES_LIMIT, MAX_PAGES_LIMIT, MAX_PAGES_LIMIT),
        concurrency: clampInteger(concurrency, 1, 5, 5),
        idempotencyKey: key,
      });
      setSelectedJobId(data.crawlId);
      if (data.idempotent) {
        showInfo("Crawl already submitted — continuing existing job.");
      } else {
        await subscribe(data.crawlId, { progressPath: "/api/crawl/progress" });
      }
      await loadJobs(data.crawlId);
      showInfo("Crawl queued. You can leave this page and return to the saved job later.");
    } catch (err) {
      const apiData = err?.response?.data || {};
      if (apiData?.code === "PLAN_FEATURE_REQUIRED") {
        setPlanGate(apiData);
      }
      setLaunching(false);
      setLaunchError(parseApiError(err, "Failed to start site crawl"));
      showError(parseApiError(err, "Failed to start site crawl"));
    }
  };

  const summaryCards = [
    { title: "Pages Crawled", value: formatNumber(summary.pages_crawled), subtitle: "Stored page snapshots", color: "primary" },
    { title: "Orphan Pages", value: formatNumber(summary.orphan_pages), subtitle: "In sitemap, unreachable by links", color: "warning" },
    { title: "Weak Pages", value: formatNumber(summary.weak_pages), subtitle: "Pages with <= 1 inbound link", color: "danger" },
    { title: "Redirect Chains", value: formatNumber(summary.redirect_chain_pages), subtitle: "Pages ending in redirects", color: "success" },
  ];

  return (
    <div className="page page-dashboard page-crawl">
      <h1 className="page-title">Site Crawl</h1>
      <p className="dashboard-lead">
        Launch a premium crawl, monitor progress, and inspect page-level SEO relationships like depth, weak internal linking, redirect chains, and orphaned URLs.
      </p>

      <div className="card crawl-launch-card" style={{
        marginBottom: 30,
        position: "relative",
        overflow: "hidden",
        minHeight: !isPremium ? "260px" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}>
        {!isPremium && (
          <PlanGateBanner
            title="Premium Site Crawl"
            description="Site Crawl is available on Premium plans. Run site-wide crawl jobs, explore page depths, check internal links, and analyze redirects."
          />
        )}

        <form onSubmit={startCrawl} style={{ opacity: !isPremium ? 0.3 : 1, pointerEvents: !isPremium ? "none" : "auto" }}>
          <div className="section-actions" style={{ marginBottom: 16 }}>
            <div>
              <h2 className="dashboard-chart-title" style={{ marginTop: 0 }}>Launch a crawl</h2>
              <p className="dashboard-chart-desc">
                Start with the homepage. The worker will follow internal links, honor `robots.txt`, and persist results for later review.
              </p>
            </div>
          </div>

          <div className="crawl-launch-grid">
            <div className="crawl-launch-main">
              <div className="filter-label">Root URL</div>
              <SimpleUrlInput
                value={url}
                onChange={setUrl}
                disabled={!isPremium || launching}
                required
                placeholder="your-website.com"
              />
            </div>
            <div>
              <div className="filter-label">Max Pages</div>
              <input
                className="input"
                type="number"
                min={MIN_PAGES_LIMIT}
                max={MAX_PAGES_LIMIT}
                step="1"
                value={maxPages}
                onChange={handleMaxPagesChange}
                onBlur={handleMaxPagesBlur}
                disabled={!isPremium || launching}
              />
            </div>
            <div>
              <div className="filter-label">Concurrency</div>
              <select
                className="input"
                value={concurrency}
                onChange={(event) => setConcurrency(event.target.value)}
                disabled={!isPremium || launching}
              >
                {CONCURRENCY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="crawl-launch-btn-container">
              <button type="submit" className="btn btn-primary" disabled={launching} style={{ width: "100%" }}>
                {launching ? "Launching..." : "Start Crawl"}
              </button>
            </div>
          </div>

        {launchError ? <div className="progress-error" style={{ marginTop: 16 }}>{launchError}</div> : null}
        {planGate?.cta ? <div className="hint" style={{ marginTop: 16 }}>{planGate.cta}</div> : null}
      </form>
    </div>

      {currentProgress && (isRunning || currentProgress.done || currentProgress.error) ? (
        <div className="card progress-container" style={{ marginBottom: 24 }}>
          <div className="progress-info">
            <span className="progress-label">{currentProgress.label || "Running crawl..."}</span>
            <span className="progress-percent">{Math.round(currentProgress.percent || 0)}%</span>
          </div>
          <div className="progress-bar-track">
            <div className="progress-bar-fill" style={{ width: `${Math.max(2, Number(currentProgress.percent || 0))}%` }} />
          </div>
          {currentProgress.stats ? (
            <div className="crawl-progress-stats">
              <span>Total queued: {formatNumber(currentProgress.stats.total)}</span>
              <span>Pending: {formatNumber(currentProgress.stats.pending)}</span>
              <span>Processing: {formatNumber(currentProgress.stats.processing)}</span>
              <span>Processed: {formatNumber(currentProgress.stats.processed)}</span>
              <span>Blocked: {formatNumber(currentProgress.stats.blocked)}</span>
              <span>Failed: {formatNumber(currentProgress.stats.failed)}</span>
            </div>
          ) : (
            <div className="progress-steps">The crawl state is persisted in the database and can recover after disconnects or restarts.</div>
          )}
        </div>
      ) : null}

      <div className="dashboard-charts crawl-overview-grid">
        <div className="card dashboard-chart-card crawl-jobs-card">
          <h2 className="dashboard-chart-title">Recent crawl jobs</h2>
          <p className="dashboard-chart-desc">Open any job to inspect its stored summary and crawled pages.</p>
          {jobsLoading ? <div className="loading">Loading crawl jobs...</div> : null}
          {!jobsLoading && filteredJobs.length === 0 ? (
            <div className="dashboard-chart-empty">No crawl jobs yet. Start your first premium crawl above.</div>
          ) : null}
          {!jobsLoading && filteredJobs.length > 0 ? (
            <div className="crawl-job-list">
              {filteredJobs.map((job) => {
                const isSelected = job.id === selectedJobId;
                return (
                  <button
                    type="button"
                    key={job.id}
                    className={`crawl-job-item ${isSelected ? "active" : ""}`}
                    onClick={() => {
                      setSelectedJobId(job.id);
                      setPagesPage(1);
                    }}
                  >
                    <div className="crawl-job-head">
                      <span className="crawl-job-url">{shortUrl(job.root_url)}</span>
                      <span className={`status-pill ${job.status === "completed" ? "healthy" : job.status === "failed" ? "action" : ""}`}>
                        {job.status}
                      </span>
                    </div>
                    <div className="crawl-job-meta">
                      <span>{formatDateTime(job.created_at)}</span>
                      <span>{formatNumber(job.max_pages)} page cap</span>
                      <span>{formatNumber(job.concurrency)} workers</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="card dashboard-chart-card">
          <h2 className="dashboard-chart-title">Crawl summary</h2>
          <p className="dashboard-chart-desc">Stored metrics for the selected job.</p>
          {!selectedJob ? (
            <div className="dashboard-chart-empty">Choose a crawl job to inspect the stored report.</div>
          ) : (
            <>
              <div className="kpi-grid kpi-grid-compact crawl-kpi-grid">
                {summaryCards.map((card) => (
                  <KPICard
                    key={card.title}
                    title={card.title}
                    value={card.value}
                    subtitle={card.subtitle}
                    color={card.color}
                    icon={card.icon}
                    size="small"
                  />
                ))}
              </div>
              <div className="crawl-summary-band">
                <div>
                  <div className="dashboard-compare-label">Root URL</div>
                  <div className="dashboard-compare-url">{selectedJob.root_url}</div>
                </div>
                <div>
                  <div className="dashboard-compare-label">Status</div>
                  <div className="dashboard-compare-url">{selectedJob.status}</div>
                </div>
                <div>
                  <div className="dashboard-compare-label">Max Depth</div>
                  <div className="dashboard-compare-url">{formatNumber(summary.max_depth)}</div>
                </div>
                <div>
                  <div className="dashboard-compare-label">Avg. Inbound Links</div>
                  <div className="dashboard-compare-url">{summary.avg_inbound_links || "0.00"}</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="dashboard-charts">
        <div className="card dashboard-chart-card">
          <div className="section-actions">
            <div>
              <h2 className="dashboard-chart-title">Issue snapshots</h2>
              <p className="dashboard-chart-desc">A quick sample from the stored report for triage and QA.</p>
            </div>
          </div>
          {samplePages.length === 0 ? (
            <div className="dashboard-chart-empty">Completed crawl jobs will surface representative pages here.</div>
          ) : (
            <div className="crawl-sample-list">
              {samplePages.slice(0, 8).map((page) => (
                <div key={page.normalized_url} className="crawl-sample-item">
                  <div className="crawl-sample-url">{page.normalized_url}</div>
                  <div className="crawl-sample-meta">
                    <span>Depth {formatNumber(page.depth)}</span>
                    <span>{formatNumber(page.inbound_link_count)} inbound</span>
                    <span className={`status-pill ${statusTone(page.fetch_status)}`}>{page.fetch_status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card dashboard-chart-card">
          <h2 className="dashboard-chart-title">How to use this report</h2>
          <p className="dashboard-chart-desc">The stored crawl data is most useful when treated as an internal linking map, not just a URL inventory.</p>
          <ul className="plain-list">
            <li>Start with `orphan` to find sitemap URLs your link graph never reached.</li>
            <li>Use `weak` to prioritize pages with almost no internal support.</li>
            <li>Check `redirect` pages so internal links point directly to final destinations.</li>
            <li>Review `failed` and `blocked` separately so robots issues do not hide fetch failures.</li>
          </ul>
        </div>
      </div>

      {isPremium && selectedJob && (pagesLoading || pagesResult.data.length > 0 || appliedSearch || issueFilter || fetchStatus || pagesError) ? (
        <div className="card">
          <div className="section-actions" style={{ marginBottom: 16 }}>
            <div>
              <h2 className="dashboard-chart-title">Page explorer</h2>
              <p className="dashboard-chart-desc">Filter the crawled page set by issue, fetch status, or URL search.</p>
            </div>
          </div>

          <div className="filter-group crawl-filter-group">
            <div>
              <div className="filter-label">Search pages</div>
              <input
                className="input"
                value={pageSearch}
                onChange={(event) => setPageSearch(event.target.value)}
                placeholder="Search URL or title"
              />
            </div>
            <div>
              <div className="filter-label">Issue</div>
              <select
                className="input"
                value={issueFilter}
                onChange={(event) => {
                  setIssueFilter(event.target.value);
                  setPagesPage(1);
                }}
              >
                <option value="">All</option>
                <option value="orphan">Orphan</option>
                <option value="weak">Weak</option>
                <option value="redirect">Redirect chains</option>
                <option value="blocked">Blocked</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <div className="filter-label">Fetch status</div>
              <select
                className="input"
                value={fetchStatus}
                onChange={(event) => {
                  setFetchStatus(event.target.value);
                  setPagesPage(1);
                }}
              >
                <option value="">All</option>
                <option value="fetched">Fetched</option>
                <option value="blocked_robots">Blocked by robots</option>
                <option value="failed">Failed</option>
                <option value="non_html">Non-HTML</option>
              </select>
            </div>
            <div className="crawl-filter-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setPagesPage(1);
                  setAppliedSearch(pageSearch.trim());
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>

          {pagesError ? <div className="progress-error">{pagesError}</div> : null}
          {pagesLoading ? <div className="loading">Loading crawled pages...</div> : null}
          {!pagesLoading && pagesResult.data.length === 0 ? (
            <div className="dashboard-chart-empty">No pages matched the current filters.</div>
          ) : null}
          {!pagesLoading && pagesResult.data.length > 0 ? (
            <>
              <DataTable
                headers={[
                  { key: "normalized_url", label: "Page", render: (page) => (
                    <>
                      <div className="crawl-table-url">{page.normalized_url}</div>
                      <div className="audit-subtext">{page.title || page.final_url || "Untitled page"}</div>
                    </>
                  ) },
                  { key: "depth", label: "Depth", render: (page) => formatNumber(page.depth) },
                  { key: "fetch_status", label: "Status", render: (page) => (
                    <span className={`status-pill ${statusTone(page.fetch_status)}`}>{page.fetch_status}</span>
                  ) },
                  { key: "inbound_link_count", label: "Inbound", render: (page) => formatNumber(page.inbound_link_count) },
                  { key: "internal_link_count", label: "Links", render: (page) => formatNumber(page.internal_link_count) },
                  { key: "flags", label: "Flags", render: (page) => (
                    <div className="crawl-flag-list">
                      {page.in_sitemap ? <span className="tag">Sitemap</span> : null}
                      {Number(page.inbound_link_count || 0) <= 1 ? <span className="tag">Weak</span> : null}
                      {Array.isArray(page.redirect_chain) && page.redirect_chain.length > 1 ? <span className="tag">Redirect</span> : null}
                    </div>
                  ) }
                ]}
                data={pagesResult.data}
                keyExtractor={(page) => `${page.job_id}-${page.normalized_url}`}
              />
              {pagesResult.pagination && pagesResult.pagination.totalPages > 1 ? (
                <div className="pagination">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={pagesPage <= 1}
                    onClick={() => setPagesPage((value) => value - 1)}
                  >
                    {"<- Prev"}
                  </button>
                  <span className="pagination-info">
                    Page {pagesResult.pagination.page} of {pagesResult.pagination.totalPages}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={pagesPage >= pagesResult.pagination.totalPages}
                    onClick={() => setPagesPage((value) => value + 1)}
                  >
                    {"Next ->"}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
